import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http'; 

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../shared/dialogs/components/message-dialog.component';
import { Observable, Subject } from 'rxjs';

import { SubjectRelay } from '../../../shared/commands/subject-relay.class';

import { EntrepriseService } from '../services/entreprise.service';

import { EntrepriseTLR } from '../entities/entreprise-tlr.entity';
import { CategoryBuilder } from '../../../shared/suggestable-input/components/suggestable-input.component';
import { ActivePossibilities } from '../../../shared/suggestable-input/entities/active-possibilities.entity';
import { SuggestionNomEntreprise } from '../entities/suggestion-nom-entreprise.entity';

import { applicationConstants } from '../../../app.constants';

enum Mode {
	CREATION,
	UPDATE
}

@Component({
	selector: 'edit-entreprise',
	templateUrl: 'edit-entreprise.html',
	styleUrls: ['edit-entreprise.css']
})
export class EditEntrepriseComponent {
	
	readonly applicationConstants = applicationConstants;
	
	// Element de comportement de la fenetre
	popupVisibility$ = new Subject<boolean>();
	modes = Mode;
	mode!: Mode;
	title!: string;
	waitingForServer: boolean = false;

	// Elements de controle du comportement des suggestions de noms d'intervenant
	entreprise!: EntrepriseTLR;

	selectionDocumentApplicable!: boolean[];
	
	observablePourSuggestionNom = (criteria: string) => this.entrepriseService.getSuggestionsNomsEntreprises(criteria, 15);
	suggestionCategoryBuilders: CategoryBuilder<ActivePossibilities, SuggestionNomEntreprise>[] = 	[
		{
			titleGetter: (_possibles: ActivePossibilities) => "Noms similaires:",
			suggestionsFilter: (_suggestion: SuggestionNomEntreprise) => false,
		},
		{
			titleGetter: (possibles: ActivePossibilities) => "Actifs (" + possibles.actifs + ")",
			suggestionsFilter: (suggestion: SuggestionNomEntreprise) => !suggestion.obsolete,
		},
		{
			titleGetter: (possibles: ActivePossibilities) => "Inactifs (" + possibles.inactifs + ")",
			suggestionsFilter: (suggestion: SuggestionNomEntreprise) => suggestion.obsolete,
		},
	];
	getSuggestionsLabel = (suggestion: SuggestionNomEntreprise) => '<strong>' + suggestion.nom + '</strong>';
	getSuggestionsLabelForInput = (suggestion: SuggestionNomEntreprise) => suggestion.nom;

	constructor(private dialog: MatDialog,
		@Inject(EntrepriseService) private entrepriseService: EntrepriseService) {

		this.openRequest$.subscribe(
			(entreprise) => {
				
				this.selectionDocumentApplicable = new Array(applicationConstants.documentApplicable.length).fill(false);
				
				if (entreprise === null) {
					this.mode = Mode.CREATION;

					// Definition du titre de la pop-up
					this.title = 'Création d\'une entreprise';

					this.entreprise = new EntrepriseTLR();
					this.entreprise.obsolete = false;
					this.entreprise.documentApplicable = applicationConstants.documentApplicable[0].value;
					this.selectionDocumentApplicable[0] = true;
				} else {
					this.mode = Mode.UPDATE;

					// Definition du titre de la pop-up
					this.title = 'Modification de l\'entreprise ' + entreprise.nom;
					this.entreprise = { ...entreprise } as EntrepriseTLR;
					this.majSelectionDocumentApplicable(entreprise.documentApplicable);
			}
			// Affichage de la pop-up
			this.popupVisibility$.next(true);
		});
	}

	// Liaison de l'observable alertante des changements de filtres
	private openRequest$ = new SubjectRelay<EntrepriseTLR | null>();
	@Input() set openRequests(obs: Observable<EntrepriseTLR | null> | undefined) {
		this.openRequest$.registerSource(obs);
	}

	// Evenements à emettre
	@Output() entrepriseCreated = new EventEmitter<void>();
	@Output() entrepriseUpdated = new EventEmitter<void>();
	
	/////////////////////////
	// Actions utilisateur //
	/////////////////////////
	
	save() {
		
		let pdpApplicable = this.selectionDocumentApplicable[0];
		let paApplicable = this.selectionDocumentApplicable[1];
		this.entreprise.documentApplicable = '';
		
		if (pdpApplicable && paApplicable) {
			this.entreprise.documentApplicable = 'Both';
		} else if (pdpApplicable) {
			this.entreprise.documentApplicable = 'PlanPrevention';
		} else if (paApplicable) {
			this.entreprise.documentApplicable = 'PersonneAvertie';
		}
		
		if (this.entreprise.documentApplicable === '') {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: "Formulaire incomplet",
					messages: ['Veuillez choisir au moin un document applicable']
				}
			});
		} else {
			this.waitingForServer = true;
			if (this.mode === Mode.CREATION) {
				this.entrepriseService.create(this.entreprise).subscribe(
					() => {
						this.waitingForServer = false;
						this.popupVisibility$.next(false);
						this.entrepriseCreated.emit();
					},
					(response: HttpErrorResponse) => {
						this.waitingForServer = false;
						let message = "Le serveur a rencontré une erreur imprévue";
						if (response.error && response.error.message) {
							message = response.error.message;
						}					
						this.dialog.open(MessageDialogComponent, {
							width: '400px',
							data: {
								title: "Erreur lors de la création de l'entreprise",
								messages: [message]
							}
						});
						console.error("Erreur lors la création de l'entreprise", response);
					}
				);
			} else {
				this.entrepriseService.update(this.entreprise).subscribe(
					() => {
						this.waitingForServer = false;
						this.popupVisibility$.next(false);
						this.entrepriseUpdated.emit();
					},
					(response: HttpErrorResponse) => {
						this.waitingForServer = false;
						let message = "Le serveur a rencontré une erreur imprévue";
						if (response.error && response.error.message) {
							message = response.error.message;
						}					
						this.dialog.open(MessageDialogComponent, {
							width: '400px',
							data: {
								title: "Erreur lors de la modification de l'entreprise",
								messages: [message]
							}
						});
						console.error("Erreur lors la modification de l'entreprise", response);
					}
				);
			}
		}
		
	}
	
	onSuggestionNomSelected(suggestion: SuggestionNomEntreprise | undefined) {
		if (suggestion != null) {
			this.entrepriseService.getAllEntreprises().subscribe(
				(entreprises) => {
					const e = entreprises.find( $ => $.id === suggestion.id);
					if (e) {
						this.switchToUpdateMode(e);
					} else {
						console.log('L\'entreprise ', suggestion.id, ' n\'a pas été retrouvé, on reste en mode création');
					}
				}
			);
		}
	}
	
	private switchToUpdateMode(entreprise: EntrepriseTLR) {
		this.popupVisibility$.next(false);

		// RDG: quand on switche sur une modification, on doit activer l'entreprise s'il ne l'est pas deja
		// Pour cela on va recopier l'entreprise, mais en changeant son status, avant d'afficher la vue
		let activeEntreprise = { ...entreprise } as EntrepriseTLR;
		activeEntreprise.obsolete = false;
		
		this.openRequest$.next(activeEntreprise);
	}
	
	majSelectionDocumentApplicable(value: string) {
		if (value === 'Both') {
			this.selectionDocumentApplicable = [true, true];
		} else if (value == 'PlanPrevention') {
			this.selectionDocumentApplicable = [true, false]
		} else if (value == 'PersonneAvertie') {
			this.selectionDocumentApplicable = [false, true]
		}
	}
}